import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { TID } from "../constants/catalog";
import { LogIn, Plus, MessagesSquare, Search, User, Shield } from "lucide-react";

const itemCls = ({ isActive }) =>
  `pbp-btn-ghost ${isActive ? "bg-[#e8e8ed]" : ""}`;

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [pending, setPending] = useState(0);

  useEffect(() => {
    if (!user?.is_admin) return;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const r = await api.get("/admin/pending-count");
        if (!cancelled) setPending((r.data?.pending_review || 0) + (r.data?.reports_open || 0));
      } catch {}
    };
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user?.is_admin]);

  return (
    <header className="sticky top-0 z-50 pbp-glass-nav border-b border-black/5">
      <div className="pbp-section flex items-center justify-between h-14 md:h-16">
        <Link to="/" data-testid={TID.nav.logo} className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#1d1d1f] grid place-items-center text-white text-[11px] font-bold tracking-tight">
            Pp
          </div>
          <span className="text-[15px] md:text-base font-semibold tracking-tight text-[#1d1d1f]">
            PartByPart
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <NavLink to="/browse" className={itemCls} data-testid={TID.nav.browse}>
            <Search className="w-4 h-4" strokeWidth={1.5} /> Browse
          </NavLink>
          <NavLink to="/sell" className={itemCls} data-testid={TID.nav.sell}>
            <Plus className="w-4 h-4" strokeWidth={1.5} /> List a part
          </NavLink>
          {user && (
            <NavLink to="/chats" className={itemCls} data-testid={TID.nav.chats}>
              <MessagesSquare className="w-4 h-4" strokeWidth={1.5} /> Chats
            </NavLink>
          )}
          {user?.is_admin && (
            <NavLink to="/admin" className={itemCls} data-testid="nav-admin">
              <Shield className="w-4 h-4" strokeWidth={1.5} /> Admin
              {pending > 0 && (
                <span
                  data-testid="nav-admin-badge"
                  className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-[#ff9500] text-white text-[10px] font-bold leading-none"
                  title={`${pending} item(s) need your attention`}
                >
                  {pending > 99 ? "99+" : pending}
                </span>
              )}
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {!user ? (
            <button
              data-testid={TID.nav.login}
              onClick={() => nav("/login")}
              className="pbp-btn-primary text-[13px] md:text-[15px]"
            >
              <LogIn className="w-4 h-4" strokeWidth={1.8} /> Sign in
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <Link
                to="/profile"
                data-testid={TID.nav.profile}
                className="flex items-center gap-2 pbp-btn-ghost"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-[#0071e3] grid place-items-center text-white text-xs font-semibold">
                    {user.name?.[0]?.toUpperCase() || <User className="w-4 h-4" />}
                  </div>
                )}
                <span className="hidden sm:inline text-sm font-medium">{user.name?.split(" ")[0]}</span>
              </Link>
              <Link to="/settings" data-testid="nav-settings" className="hidden md:inline-flex pbp-btn-ghost text-[13px]">Settings</Link>
              <button
                data-testid={TID.nav.logout}
                onClick={async () => {
                  await logout();
                  nav("/");
                }}
                className="hidden md:inline-flex pbp-btn-ghost text-[13px]"
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
